import { HomeNavbar } from "../shared/navbar";
import styles from "./styles.module.scss";

export function TermsPage() {
  return (
    <>
      <HomeNavbar />
      <main className={styles.main}>
        <Terms />
      </main>
    </>
  );
}

/** This gets used by the accept terms & conditions auth form. */
export const Terms = () => (
  <>
    <h2>Terms and Conditions</h2>

    <p>
      These Terms and Conditions (“Terms”) describe your rights and responsibilities when using the Flueid Pro
      application, via the https://pro.flueid.com website (“Site) or any current and future services, products,
      information, applications, software and/or data provided by Flueid Software Corporation (“Flueid”) on the Site
      (collectively, “Services”) or downloading, installing or using our iPhone application.
    </p>

    <p>
      By accessing, downloading, installing, running or using the Services, including any data and images contained
      therein, you acknowledge that you have reviewed and accept the Terms. If you do not agree to our Terms, you should
      not access or otherwise use the Services. The Services are only available for use by valid business entities,
      including sole proprietorships. The terms “you” or “your” shall refer to the business entity that you represent
      and the terms “we,” “us,” or “our” shall refer to Flueid. You represent and warrant that you have the authority to
      bind that entity.
    </p>

    <p>
      By using the Services, you represent, acknowledge and agree that you: (a) are 18 years of age or older (or the
      applicable age of majority, if greater than eighteen (18) years of age in your jurisdiction of residence); (b) can
      enter into legally binding contracts under applicable law; (c) are not a resident of, or physically located in,
      the United Kingdom or any European Union Member State; and (d) are acting in your capacity as a duly authorized
      representative of a valid business entity (collectively, “Usage Requirements”). If you do not satisfy all of the
      Usage Requirements, you may not access and use the Services.
    </p>

    <p>
      <b>1. Accounts. When you create an account with us:</b>
    </p>
    <ul className={styles.alpha_list}>
      <li>
        you must provide us information that is accurate, complete, and current at all times. Failure to do so
        constitutes a breach of the Terms, which may result in immediate termination of your account on our Service;
      </li>
      <li>
        You are responsible for safeguarding the password that you use to access the Service and for any activities or
        actions under your password, whether your password is with our Service or a third-party service;
      </li>
      <li>
        You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware
        of any breach of security or unauthorized use of your account;
      </li>
      <li>
        You may not use as a username the name of another person or entity or that is not lawfully available for use, a
        name or trademark that is subject to any rights of another person or entity other than you without appropriate
        authorization, or a name that is otherwise offensive, vulgar or obscene; and
      </li>
      <li>
        You agree that we may contact you by telephone or email as provided during registration regarding your account
        and the Services at any time during the term of this agreement.
      </li>
    </ul>

    <p>
      You may contact Flueid Customer Service by e-mail at support@flueid.com or telephone at (888) 835-8343 concerning
      charges or other questions regarding the status of your account.
    </p>

    <p>
      <b>2. Billing/Payment.</b> As applicable, you understand that billing will occur as a monthly subscription fee as
      outlined during the signup process along with any upgrades, enhancements, or fee based services that you may agree
      to within the Service or from the order page on the Site until you cancel your subscription. You may cancel at any
      time by contacting Customer Support at (888) 835-8343. You also fully understand and agree that all pre-paid,
      monthly subscriptions are non-refundable.
    </p>

    <p>
      You are required to pay all charges on time and agree to submit an accompanying payment authorization in
      connection with these charges when requested by Flueid. Credit card transactions require an acceptable and
      currently working/continuously valid credit card number/account. Flueid may terminate or disable the Services if
      you fail to pay fully and in a timely manner any and all amounts due to Flueid. If your credit card expires or is
      otherwise declined for payment, access to the Services can be modified or suspended without notice to you. Flueid
      may, if necessary, extend the expiration of your credit card in order to assure continued service. In the event
      that your designated primary payment method declines for any subscription fee or additional charge that you may
      incur, you authorize Flueid to bill any and all additional credit cards or payment methods you may have on file to
      satisfy any outstanding balance(s).
    </p>

    <p>
      Payment processing services for your subscription are provided by Stripe, Inc. (“Stripe”), a technical service
      provider and may offer the services as an agent of one or more financial institutions in the United States. By
      accepting this Agreement, you are also accepting and agreeing to be bound by the Stripe Connected Account
      Agreement, which includes the Stripe Terms of Service (collectively, “Stripe Services Agreement”) and which may be
      modified by Stripe from time to time. As a condition of utilizing our Services, you agree to provide us with
      accurate and complete information about you and your business, and you authorize us to share such information,
      including transaction information, related to your use of the payment processing services provided by Stripe.
    </p>

    <p>
      Flueid is not a party to the Stripe Services Agreement and is not liable to you in respect thereof. By accepting
      this Agreement and the Stripe Services Agreement you are agreeing to the creation of an account with the Stripe
      for payment processing. We reserve the right to change the processor, subject to the terms of our agreement with
      processor. In the event of any inconsistency between this Agreement and the Stripe Services Agreement, this
      Agreement shall prevail, except in the event of any inconsistency between this Agreement and the Stripe Services
      Agreement concerning payment processing, in which case the Processor Terms shall prevail.
    </p>

    <p>
      <b>3. License.</b> For as long as you remain compliant with all Terms as set forth herein, and contingent upon
      your required registration and use of the Services solely as a non-consumer institutional end-user, we grant you a
      limited, non-exclusive, non-transferrable, revocable license to access and use our Site, including the data and
      information available therein, for your own internal business use. You will not permit any other party or entity,
      for any reason, to use said Services on your behalf. The Site may be used for no other purposes and the Services
      are provided for your informational purposes only. Any rights not expressly granted herein are reserved by us,
      including our licensors. This license does not permit you to rent, lease, lend, sell, redistribute or sublicense
      our Site and Services.
    </p>

    <p>
      <b>4. License Restrictions.</b> You represent, warrant, affirm and agree that the data, information and services
      provided by Flueid will only be used by you and that you will not permit or allow the Services to be used by any
      agent, representative, consultant, officer, director, shareholder, parent organization, subsidiary organization,
      third party or any other person or entity unless you have the appropriate subscription allowing such expanded use.
      You acknowledge Flueid’s right to monitor your search volume to avoid abuse, data duplication, “data mining”, and
      excessive or otherwise non-customary search and usage patterns. Any such actions will be determined by Flueid in
      its sole discretion and will result in an immediate termination of your account. You further represent, warrant,
      affirm and agree that you will not transfer, sell, convey, use, resell, copy, transfer, publish, redistribute,
      reproduce or sublicense the Services provided to you by Flueid in any medium, form, manner or format whatsoever,
      for any purpose including, but not limited to the following:
    </p>

    <ul className={styles.alpha_list}>
      <li>
        Reproduction, reformatting, publication, distribution or dissemination associated with any service or product
        provided or made available to any third party;
      </li>
      <li>Creation of derivative products;</li>
      <li>
        Marketing or telemarketing uses other than for your own marketing and management purposes, provided you do not
        utilize property listing data or images for such purposes;
      </li>
      <li>World Wide Web, Internet or online uses;</li>
      <li>Real estate valuation models, programs or systems;</li>
      <li>Inclusion or in combination with any other service or product of any kind;</li>
      <li>
        Archival of any portion of the Services available through the Site to create, directly or indirectly, a
        competitive product or any real estate database compilation for commercial exploitation;
      </li>
      <li>Real estate appraisal;</li>
      <li>
        For title underwriting purposes or supporting alternative title products (e.g. title waiver and attorney opinion
        letters)
      </li>
      <li>
        Evaluating risk, marketing sale of insurance products of any kind, including but not limited to mortgage, title
        or property;
      </li>
      <li>Debt collection;</li>
      <li>
        Shall not use any portion of the Services in conjunction with platforms that result in the sharing of the
        Services outside of the authorized use set forth in this Agreement, including, without limitation artificial
        intelligence applications from any other source, artificial intelligence chatbots, auto-generative systems, or
        similar language processing tools such as ChatGPT, Google Gemini, or similar tools; and
      </li>
      <li>
        As a factor in establishing an individual’s eligibility for credit, insurance, or employment; in connection with
        a determination of an individual’s eligibility for a license or other benefit granted by a governmental
        authority; in connection with underwriting individual insurance or coupled with alternative insurance
        approaches; or in a way that would cause the Services to constitute a “consumer report” under the Fair Credit
        Reporting Act, 15 U.S.C. Â§ 1681 et seq. or similar statute, or by any other authority having jurisdiction over
        the parties.
      </li>
    </ul>

    <p>
      <b>5. Site Content and Materials.</b> The information maintained on the Site is intended to provide users with
      information about our Services and is subject to change without notice. The Services described in the Site may not
      be available in all geographic areas. For details as to the Services offered, including the related pricing,
      please refer to the applicable product details found within the Site. Our Services may not be in any jurisdiction
      where the provision of such Services, including the information, materials, products or services associated
      therewith, is prohibited by law.
    </p>

    <p>
      <b>6. Data Validation.</b> Information on the Site is deemed to be valid and reliable, but is not guaranteed. It
      is your sole responsibility to independently confirm and verify all information upon which you may rely or use
      from the Site.
    </p>

    <p>
      <b>7. Inaccurate Data.</b> In the event you find any property listing data or information from, through or as a
      part of our Services to be inaccurate or incomplete, you shall notify us.
    </p>

    <p>
      <b>8. Technological Issues.</b> The Site may, from time to time, not operate as intended, at normal speed, with
      full functionality, or at all. In our sole discretion, the Site and our Services may cease to operate for any
      amount of time, and limitations upon the functionality or use of such Site and Services by the end user may
      result. Any decision to cease the activity of any one or all of our Services is solely at our discretion.
    </p>

    <p>
      <b>9. Compliance – Governing Laws.</b> As a condition of your use of our Services, you represent and warrant to us
      that you will:
    </p>

    <ul className={styles.alpha_list}>
      <li>not use our Services for any purpose that is unlawful or prohibited by the Terms;</li>
      <li>
        Comply with all applicable federal, state and local laws and regulations in connection with the use of the
        Services, including, without limitation, all applicable email marketing, privacy, and data protection laws; and
      </li>
      <li>
        Use the Services in compliance with all applicable laws, including the Fair Credit Reporting Act, 15 U.S.C. §
        1681 et seq., as amended ("FCRA"), the Telephone Consumer Protect Action 1991, 47 U.S.C. § 227 (“TCPA”), the
        rules and regulations promulgated by the Federal Communications Commission implementing same located at 47
        C.F.R. § 64.1200 and any applicable state laws and regulations regarding the sending of prerecorded messages or
        texts, including any applicable “Do Not Call” prohibitions or regulations, the Equal Credit Opportunity Act, 15
        U.S.C. § 1691 et seq. ("ECOA"), the Telemarketing Sales Rule (“TSR”), the California Invasion of Privacy Act
        (“CIPA”), the California Consumer Privacy Act of 2018 (“CCPA”), the California Privacy Rights Act (“CPRA”), the
        Colorado Privacy Act (“CPA”), the Virginia Consumer Data Protect Act (“VCDPA”), the Connecticut Personal Data
        Privacy and Online Monitoring Act (“CTDPA”), and the Utah Consumer Privacy Act (“UCPA”), and any other
        applicable state privacy law;
      </li>
      <li>
        Obtain the necessary “prior written consent” from each individual you contact in connection with the Services in
        accordance with the TCPA to receive commercial telephone calls (including autodialed calls, pre-recorded calls,
        artificial voice calls and/or SMS text messages) from you (“TCPA Consent”);
      </li>
      <li>
        Collect evidence of TCPA Consent for each call recipient including name, date, time, IP address and referral URL
        where the applicable consumer(s) submitted the TCPA Consent, as well as the contents of the registration form
        used to obtain TCPA Consent (collectively, the “TCPA Consent Records”). You shall maintain TCPA Consent Records
        for a minimum of six (6) years following collection of same. You must, within two (2) business days of receipt
        of Our request, provide Us with the TCPA Consent Records; and
      </li>
      <li>
        Not use our Services in any manner that could damage, disable, overburden, compromise or impair our Services or
        interfere with any other party's use and enjoyment of our Services.
      </li>
    </ul>

    <p>
      <b>10. Privacy Policy/Data.</b> We respect the information that you provide to us, and want to be sure you fully
      understand exactly how We use that information. Please review our Privacy Policy (“Privacy Policy”) which explains
      how we may use the information you provide to us.
    </p>

    <p>
      We are acting as a service provider to you when we provide the Services. To the extent the Services involve Flueid
      processing personal information on your behalf, we agree:
    </p>

    <ul>
      <li>
        To only process personal information for the purposes of providing the Services and will not retain, use, or
        disclose personal information for any other purpose or otherwise outside of the direct business relationship
        between us; and
      </li>
      <li>
        Not to sell, rent, release, disclose, disseminate, make available, transfer, or otherwise communicate personal
        information to any third party for monetary or other valuable consideration (excluding our service providers who
        are providing services to us).
      </li>
    </ul>

    <p>We certify that We understand the requirements set forth above. Notwithstanding the foregoing, Flueid may:</p>

    <ul>
      <li>
        combine personal information received from you with personal information received from other entities to the
        extent necessary to detect security incidents or protect against fraudulent or illegal activity
      </li>
      <li>
        unless prohibited by applicable law, de-identify and/or anonymize personal information (with the resulting
        de-identified or anonymized data (“De-identified Data”) and may (subject to any restrictions under applicable
        law) aggregate such De-identified Data, including, without limitation, aggregation with other information
        received by us from other customers and from other data sources (collectively, “Aggregated Data”) for the
        purpose of providing the Services and enhancing the features, functions, and performance of the Services and
        associated content. All De-identified Data and Aggregated Data shall be owned solely and exclusively by us, and
        you acknowledge and agree that De-identified Data and Aggregated Data cease to be personal information,
        including without limitation for purposes of this Agreement, and Supplier may, during and after the term of this
        Agreement, use, reproduce, distribute, sell and otherwise commercialize such De-identified Data and Aggregated
        Data.
      </li>
    </ul>

    <p>
      <b>11. Intellectual Property.</b>
    </p>

    <ul className={styles.alpha_list}>
      <li>
        Flueid its affiliates, successors and assigns, as the developer and host of our Site, owns or licenses all other
        intellectual property rights related to our Site, including the look and feel of our Site and all underlying
        software, as updated and modified from time to time. You may not download and/or save a copy of any of the
        screens except as otherwise provided in these Terms. The Service is protected by copyright, trademark, and other
        laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in
        connection with any product or service without the prior written consent of Flueid Software Corporation. Except
        for the limited license granted herein, Flueid, including its licensors, does not grant any license or other
        authorization in the Services or to the respective trademarks, service marks, copyrightable material, trade
        secrets or other intellectual property contained within or displayed on or through our Site. The Services and
        the original content, features and functionality are and will remain the exclusive property of Flueid Software
        Corporation and its licensors.
      </li>
      <li>
        You shall (i) never remove or destroy any copyright or other proprietary marking placed upon, contained with, or
        used in connection with our Services; (ii) never create or authorize new versions, modifications, enhancements
        or derivative works to, nor translate, reverse engineer, de-compile, disassemble or attempt to derive the source
        code of, the Site and/or any of our Services or any portion thereof.
      </li>
      <li>
        You shall not copy, screen scrape, redistribute or retransmit any of the data or information provided, except
        you may use the property listing data in connection with your purchase, sale, or lease of an individual
        property.
      </li>
    </ul>

    <p>
      <b>12. Electronic Communications.</b> When you use our Services, you are communicating with us electronically. You
      expressly consent and opt in to receive communications from us electronically. We will communicate with you by
      e-mail, instant messaging, or other electronic means. You agree that all agreements, notices, disclosures, and
      other communications that we provide to you electronically satisfy any legal requirement that such communication
      be in writing.
    </p>

    <p>
      <b>13. Links To Other Web Sites.</b> Our Service may contain links to third-party web sites or services that are
      not owned or controlled by Flueid Software Corporation.
    </p>

    <p>
      Flueid Software Corporation has no control over, and assumes no responsibility for, the content, privacy policies,
      or practices of any third-party web sites or services. You further acknowledge and agree that Flueid Software
      Corporation shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged
      to be caused by or in connection with use of or reliance on any such content, goods or services available on or
      through any such web sites or services.
    </p>

    <p>
      We strongly advise you to read the terms and conditions and privacy policies of any third-party web sites or
      services that you visit.
    </p>

    <p>
      <b>14. Termination.</b> We may terminate or suspend your account immediately, without prior notice or liability,
      for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to
      use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using
      the Service.
    </p>

    <p>
      <b>15. Limitation Of Liability.</b> In no event shall Flueid Software Corporation, nor its directors, employees,
      partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or
      punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses,
      resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content
      of any third party on the Service; (iii) any content obtained from the Service; and (iv) unauthorized access, use
      or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence)
      or any other legal theory, whether or not we have been informed of the possibility of such damage, and even if a
      remedy set forth herein is found to have failed of its essential purpose.
    </p>

    <p>
      <b>16. Disclaimer of Warranties.</b> THE INFORMATION CONTAINED WITHIN OUR SERVICES, INCLUDING WITHOUT LIMITATION
      ALL TEXT, GRAPHICS, LINKS, TOOLS OR OTHER ITEMS ARE PROVIDED ON AN "AS IS", “AS AVAILABLE” BASIS. FLUEID, ITS
      SUPPLIERS, LICENSORS AND THIRD-PARTY CONTENT PROVIDERS DISCLAIM ANY REPRESENTATION, WARRANTY OR OTHER ASSURANCE
      WITH RESPECT TO THE OPERATION, QUALITY, ACCURACY, SECURITY, COMPLETENESS, TIMELINESS, FUNCTIONALITY OR TITLE TO
      CONTENT PROVIDED OR DISPLAYED, INCLUDING THE PROPERTY LISTING DATA. ACCESS TO OUR SERVICES AT ANY TIME MAY BE
      INTERRUPTED, RESTRICTED OR DELAYED FOR REASONS BEYOND OUR CONTROL. NO WARRANTY OF ANY KIND, IMPLIED, EXPRESS OR
      STATUTORY, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, TITLE, NON-INFRINGEMENT OF INTELLECTUAL
      PROPERTY, FITNESS FOR ANY PARTICULAR PURPOSE AND FREEDOM FROM COMPUTER VIRUS, IS GIVEN IN CONJUNCTION WITH OUR
      SERVICES, AND THEIR ATTENDANT INFORMATION AND MATERIALS. YOU AGREE AND ACKNOWLEDGES YOUR USE OF THE SERVICES
      PROVIDED IN, BY OR THROUGH FLUEID AND THE SITE IS AT YOUR OWN RISK. FLUEID, ITS SUPPLIERS, LICENSORS AND
      THIRD-PARTY CONTENT PROVIDERS ARE NOT LIABLE FOR ANY DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL OR
      CONSEQUENTIAL DAMAGES OR OTHER INJURY ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OF THE SERVICES OR WITH
      THE DELAY OR INABILITY TO USE THE SITE, OR FOR ANY INFORMATION, SOFTWARE, PRODUCTS, GOODS AND/OR SERVICES OBTAINED
      THROUGH THE SITE, OR OTHERWISE ARISING OUT OF THE USE OF THE SITE, WHETHER RESULTING IN WHOLE OR IN PART, FROM
      BREACH OF CONTRACT, TORTIOUS BEHAVIOR, NEGLIGENCE, STRICT LIABILITY OR OTHERWISE, REGARDLESS OF WHETHER OR NOT YOU
      KNOW, SUSPECT OR HAVE BEEN ADVISED OF THE POSSIBILITY OF ANY SUCH DAMAGES. SOME JURISDICTIONS DO NOT ALLOW THE
      EXCLUSION OF IMPLIED WARRANTIES, SO THE ABOVE EXCLUSION MAY NOT APPLY TO THE CLIENT, IF THE CLIENT RESIDES IN SUCH
      A JURISDICTION. NO ORAL ADVICE OR WRITTEN INFORMATION GIVEN BY FLUEID, ITS EMPLOYEES, AFFILIATES, LICENSORS,
      SERVICES VENDORS OR AGENTS WILL CREATE A WARRANTY; NOR MAY YOU RELY ON ANY SUCH INFORMATION OR ADVICE.
    </p>

    <p>
      You further acknowledge and understand that the data and information made available by and through the Services
      are available just for the purpose of providing general information on properties, property owners, property
      occupants, and other related issues. You should not rely on the Services and the information and resources made
      available in connection therewith as a replacement or substitute for any professional, financial, legal or other
      advice or counsel. We make no representations or warranties, and expressly disclaim any and all liability,
      concerning actions taken by a user following the information or using the resources offered or provided on or
      through the Services. We will not be responsible for any actions taken or not taken based on the information or
      resources provided by and through the Services.
    </p>

    <p>
      <b>17. Indemnification.</b> You agree to defend and indemnify Flueid, including its affiliates, officers,
      directors, employees and agents, against any and all claims, losses, damages, liability, costs and expenses
      (including but not limited to reasonable attorneys' fees) arising from (a) your use of our Services, including the
      property listing data; (b) violation of these Terms; or (c) violation of any law or third-party's rights,
      (including, without limitation, infringement of any copyright trademark, service mark, trade secrets, right of
      privacy or publicity or any other third party right). The terms of this section shall survive the termination of
      your relationship with us and/or registration for our Services.
    </p>

    <p>
      <b>18. Governing Law/Venue.</b> By using our Services, you agree that applicable federal law and the laws of the
      State of Texas, without regard to its principles of conflict of laws, will govern these Terms and any dispute of
      any sort that might arise between you and us. You agree that venue for any disputes shall be located exclusively
      in Travis County, Texas and expressly waive any jurisdiction privileges or right to claim that such forum is
      inconvenient.
    </p>

    <p>
      <b>19. Changes.</b> We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If
      a revision is material, we will try to provide at least 30 days’ notice prior to any new terms taking effect. What
      constitutes a material change will be determined at our sole discretion. By continuing to access or use our
      Services after those revisions become effective, you agree to be bound by the revised terms. If you do not agree
      to the new terms, please stop using the Services.
    </p>

    <p>We may further elect, in our sole discretion, to change, edit, or discontinue the Services without notice.</p>

    <p>
      <b>20. Non-Waiver.</b> Our failure to exercise or enforce any provision or right set forth in these Terms does not
      constitute a waiver of that right or provision.
    </p>

    <p>
      <b>21. Assignment.</b> Flueid may assign these Terms, in whole or in part, at any time with or without notice to
      you.
    </p>

    <p>
      <b>22. Headings.</b> Headings are for reference purposes only and in no way define, limit, construe or describe
      the scope or extent of such action.
    </p>

    <p>
      <b>23. Complete Agreement.</b> These Terms constitute the final, complete, and exclusive statement of the terms
      between the parties that pertain to the subject matter found herein, and these Terms supersede all prior and
      contemporaneous understandings or agreements of the parties. No party has been induced to enter into these Terms
      by, nor is any party relying on any representation or warranty independent of those expressly set forth in these
      Terms. No direct benefit is intended to be conferred by these Terms on any person not a party hereto and any
      benefit which may be actually conferred is purely incidental.
    </p>

    <p>
      <b>24. Modification of Terms.</b> We reserve the right to modify these Terms and its policies at any time without
      advance notice to you. Any modification of these Terms or any related policy is effective once displayed or
      published on or within our Services. You are responsible for regularly reviewing these documents. Continued use of
      our Services after any such changes shall constitute your consent to such changes. We do not and will not assume
      any obligation to notify you of any changes to this Terms.
    </p>

    <p>
      <b>25. Effective Date.</b> These Terms are effective June 1, 2025.
    </p>
  </>
);
